import axios from 'axios';
import { ArrowLeft, Check, Code, Copy, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppContext } from '../../contexts/useAppContext';
import { generateAnalysisPDF } from '../../utils/pdf';
import { logger } from '../../utils/logger';
import { api } from '../../services/authenticatedApi';
import type { DriftReport, RepositoryAnalysis } from '../types/analysis.types';

import { CategoryBreakdown } from './CategoryBreakdown';
import { DocumentsGrid } from './DocumentsGrid';
import { GapsDetailedSection } from './GapsDetailedSection';
import { InsightsGrid } from './InsightsGrid';
import { QuickWinsPanel } from './QuickWinsPanel';
import { RepositoryDetailsCard } from './RepositoryDetailsCard';
import { ScoreTabsPanel, type ScoreTab } from './ScoreTabsPanel';

import { fetchDriftReport, generateDriftDocument, vibeMapperDriftReport } from './drift';
import { triggerMarkdownDownload } from './agentMd';
import { agentReadyMdEnabled } from '../../config/featureFlags';
import { useCopyFeedback } from '../../hooks/useCopyFeedback';
import { OverviewRepositoryHeader } from './OverviewRepositoryHeader';

interface OverviewSectionProps {
  data: RepositoryAnalysis;
  onNavigateToRecommendations: () => void;
  onBackToRepository?: () => void;
  groundingScore?: number;
  projects?: RepositoryAnalysis[];
  selectedProject?: RepositoryAnalysis | null;
  onSelectProject?: (project: RepositoryAnalysis) => void;
}

export const OverviewSection: React.FC<OverviewSectionProps> = ({
  data,
  onNavigateToRecommendations,
  onBackToRepository,
  groundingScore,
  projects = [],
  selectedProject = null,
  onSelectProject,
}) => {
  const { jobId } = useParams<{ jobId: string }>();
  const { isDemoMode } = useAppContext();
  const [activeTab, setActiveTab] = useState<ScoreTab>('sdlc');
  const [showDebugJson, setShowDebugJson] = useState(false);
  const { copied: jsonCopied, copy: copyJson } = useCopyFeedback();
  const { copied: agentMdCopied, copy: copyAgentMd } = useCopyFeedback();
  const [isExporting, setIsExporting] = useState(false);
  const [agentMdBusy, setAgentMdBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const errorTimer = useRef<ReturnType<typeof setTimeout>>();
  const [driftData, setDriftData] = useState<DriftReport | null>(null);
  const [driftLoading, setDriftLoading] = useState(true);
  const [, setDriftError] = useState<string | null>(null);

  const isDevelopment = import.meta.env.DEV;
  const maintenanceScore = data.maintenanceScore ?? null;
  const activeReport = projects.length > 0 ? (selectedProject ?? projects[0] ?? data) : data;

  const primaryLanguage = useMemo(() => {
    if (!data.languages || data.languages.length === 0) return 'Unknown';
    const first = data.languages[0];
    return typeof first === 'string' ? first : first.name;
  }, [data.languages]);

  const isDemoRepository = isDemoMode;

  useEffect(() => {
    // Use mock data for demo repositories (no API call needed)
    if (isDemoRepository) {
      setDriftData(vibeMapperDriftReport);
      setDriftLoading(false);
      return;
    }

    let cancelled = false;

    const loadDriftData = async () => {
      if (!data.repositoryId) {
        setDriftLoading(false);
        return;
      }

      setDriftLoading(true);
      setDriftError(null);

      try {
        const report = await fetchDriftReport(
          data.repositoryId,
          data.repositoryName,
          data.repositoryDetails?.projectType,
          primaryLanguage,
        );
        if (!cancelled) {
          setDriftData(report);
        }
      } catch (error) {
        logger.error('Failed to fetch drift data', { error });
        if (!cancelled) {
          setDriftError('Failed to load drift data');
        }
      } finally {
        if (!cancelled) {
          setDriftLoading(false);
        }
      }
    };

    loadDriftData();
    return () => {
      cancelled = true;
    };
  }, [
    data.repositoryId,
    data.repositoryName,
    data.repositoryDetails?.projectType,
    primaryLanguage,
    isDemoRepository,
  ]);

  const driftDocument = useMemo(
    () =>
      driftData
        ? generateDriftDocument(driftData, maintenanceScore, {
            repositoryName: data.repositoryName,
            primaryLanguage,
          })
        : null,
    [driftData, maintenanceScore, data.repositoryName, primaryLanguage],
  );

  const dataWithDriftDocument = useMemo(
    () => ({
      ...data,
      documents: driftDocument ? [driftDocument] : [],
    }),
    [data, driftDocument],
  );

  const dataWithoutDriftDocument = useMemo(
    () => ({
      ...data,
      documents: data.documents || [],
    }),
    [data],
  );

  const handleCopyJson = () => {
    void copyJson(JSON.stringify(activeReport, null, 2));
  };

  useEffect(() => () => clearTimeout(errorTimer.current), []);

  const showTransientError = (message: string) => {
    setExportError(message);
    clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => setExportError(null), 5000);
  };

  const handleDownloadReport = async () => {
    if (isExporting) return;

    try {
      setIsExporting(true);
      setExportError(null);

      await generateAnalysisPDF(activeReport, jobId, driftData, maintenanceScore);
    } catch (error) {
      logger.error('Failed to generate report', { error });
      showTransientError('Failed to generate report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const emptyAgentMdMessage =
    'No agent instructions for this analysis yet. Re-run the scan to generate them.';

  const fetchAgentMd = async (): Promise<{ content: string; fileName: string } | null> => {
    if (!jobId) {
      logger.error('Failed to fetch agent-ready md', { error: 'missing jobId' });
      return null;
    }
    if (agentMdBusy) return null;
    setAgentMdBusy(true);
    try {
      setExportError(null);
      const project =
        activeReport.projectPath && activeReport.projectPath !== '.'
          ? activeReport.projectPath
          : undefined;
      const response = await api.get<string>(`/analysis/${jobId}/agent-md`, {
        params: project ? { project } : undefined,
        responseType: 'text',
      });
      const content = response.data ?? '';
      if (!content) {
        showTransientError(emptyAgentMdMessage);
        return null;
      }
      const name = activeReport.projectPath || activeReport.repositoryName || 'repository';
      return {
        content,
        fileName: `${name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}-fixes.md`,
      };
    } catch (error) {
      logger.error('Failed to fetch agent-ready md', { error });
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      showTransientError(
        status === 404
          ? emptyAgentMdMessage
          : 'Failed to get the agent instructions. Please try again.',
      );
      return null;
    } finally {
      setAgentMdBusy(false);
    }
  };

  const handleCopyAgentMd = async () => {
    const md = await fetchAgentMd();
    if (!md) return;
    const copied = await copyAgentMd(md.content);
    if (!copied) {
      showTransientError('Could not copy to clipboard. Try the download instead.');
    }
  };

  const handleDownloadAgentMd = async () => {
    const md = await fetchAgentMd();
    if (!md) return;
    triggerMarkdownDownload({
      fileName: md.fileName,
      title: 'Agent instructions',
      content: md.content,
    });
  };

  const copyButtonClass = jsonCopied
    ? 'bg-green-500 text-white'
    : 'bg-purple-500 text-white hover:bg-purple-600';

  return (
    <div className="view-fade-in px-4 sm:px-6 lg:px-8">
      {exportError && (
        <div className="fixed bottom-4 right-4 z-50 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-slide-up">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {exportError}
        </div>
      )}

      {/* Back to Repository */}
      {onBackToRepository && (
        <div className="max-w-7xl mx-auto mb-4">
          <button
            onClick={onBackToRepository}
            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Repository
          </button>
        </div>
      )}

      <OverviewRepositoryHeader
        data={data}
        jobId={jobId}
        isDevelopment={isDevelopment}
        showDebugJson={showDebugJson}
        onToggleDebugJson={() => setShowDebugJson(!showDebugJson)}
        isExporting={isExporting}
        onDownloadReport={handleDownloadReport}
        onCopyAgentMd={handleCopyAgentMd}
        onDownloadAgentMd={handleDownloadAgentMd}
        agentMdBusy={agentMdBusy}
        agentMdCopied={agentMdCopied}
        agentMdEnabled={agentReadyMdEnabled}
        maintenanceScore={maintenanceScore}
        driftData={driftData}
        driftLoading={driftLoading}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        projects={projects}
        selectedProject={selectedProject}
        onSelectProject={onSelectProject}
      />

      {/* Content — selected project or parent */}
      {projects.length > 0 && activeTab === 'drift' ? (
        <div className="max-w-7xl mx-auto mb-12">
          <div className="mb-8">
            <ScoreTabsPanel
              maintenanceScore={maintenanceScore}
              driftData={driftData}
              activeTab={activeTab}
            />
          </div>
          <div className="mb-12">
            <DocumentsGrid data={dataWithDriftDocument} />
          </div>
        </div>
      ) : projects.length > 0 ? (
        /* Project analysis */
        <div className="max-w-7xl mx-auto mb-12">
          <div className="mb-8">
            <InsightsGrid data={activeReport} />
          </div>
          <div className="mb-8">
            <QuickWinsPanel
              data={activeReport}
              onNavigateToRecommendations={onNavigateToRecommendations}
            />
          </div>
          <div className="mb-8">
            <GapsDetailedSection data={activeReport} />
          </div>
          <div className="mb-8">
            <RepositoryDetailsCard
              data={activeReport}
              groundingScore={!selectedProject ? groundingScore : undefined}
            />
          </div>
          <div className="mb-8">
            <CategoryBreakdown data={activeReport} />
          </div>
          <div className="mb-8">
            <DocumentsGrid data={{ ...activeReport, documents: activeReport.documents || [] }} />
          </div>
        </div>
      ) : (
        /* No projects — normal analysis */
        <>
          <div className="mb-8">
            <ScoreTabsPanel
              maintenanceScore={maintenanceScore}
              driftData={driftData}
              activeTab={activeTab}
            />
          </div>

          {activeTab === 'drift' ? (
            <div className="mb-12">
              <DocumentsGrid data={dataWithDriftDocument} />
            </div>
          ) : (
            <>
              <div className="mb-8">
                <InsightsGrid data={data} />
              </div>
              <div className="mb-8">
                <QuickWinsPanel
                  data={data}
                  onNavigateToRecommendations={onNavigateToRecommendations}
                />
              </div>
              <div className="mb-8">
                <GapsDetailedSection data={data} />
              </div>
              <div className="mb-8">
                <RepositoryDetailsCard data={data} groundingScore={groundingScore} />
              </div>
              <div className="mb-8">
                <CategoryBreakdown data={data} />
              </div>
              <div className="mb-12">
                <DocumentsGrid data={dataWithoutDriftDocument} />
              </div>
            </>
          )}
        </>
      )}

      {isDevelopment && showDebugJson && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs"
          onClick={() => setShowDebugJson(false)}
        >
          <div
            className="w-full max-w-4xl max-h-[80vh] m-4 rounded-xl shadow-2xl border border-purple-200 dark:border-purple-800/50 bg-white dark:bg-slate-900 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold flex items-center gap-2 text-primary">
                <Code className="w-5 h-5 text-purple-500" />
                Raw JSON Data
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyJson}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors ${copyButtonClass}`}
                >
                  {jsonCopied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      Copy JSON
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowDebugJson(false)}
                  className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <pre className="flex-1 overflow-auto p-4 bg-slate-900 text-green-400 text-xs font-mono">
              {JSON.stringify(activeReport, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewSection;
